import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useLanguage, type Lang } from '@/contexts/LanguageContext';
import { SEO_LOCALE_URL_STRATEGY } from '@/lib/seo';
import { swapLocalePath } from '@/lib/localeRouting';

const OPTIONS: Lang[] = ['tr', 'en'];

type LanguageSwitchProps = {
  className?: string;
  /** Compact pill for mobile header */
  compact?: boolean;
};

const LanguageSwitch = ({ className, compact = false }: LanguageSwitchProps) => {
  const { lang, setLang } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSelect = (code: Lang) => {
    if (code === lang) return;
    setLang(code);
    if (SEO_LOCALE_URL_STRATEGY === 'path') {
      const nextPath = swapLocalePath(
        `${location.pathname}${location.search}${location.hash}`,
        code,
      );
      navigate(nextPath);
    }
  };

  return (
    <div
      role="group"
      aria-label={lang === 'tr' ? 'Dil seçimi' : 'Language selection'}
      className={cn(
        'relative inline-flex rounded-full border border-border/50 bg-muted/20 p-0.5',
        compact ? 'h-8' : 'h-9',
        className,
      )}
    >
      {OPTIONS.map((code) => {
        const active = lang === code;
        return (
          <button
            key={code}
            type="button"
            onClick={() => handleSelect(code)}
            aria-pressed={active}
            className={cn(
              'relative z-10 rounded-full font-body font-medium tracking-[0.08em] transition-colors duration-200',
              compact ? 'px-2.5 text-[11px]' : 'px-3.5 text-xs',
              active ? 'text-background' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {active && (
              <motion.span
                layoutId="figtures-language-pill"
                className="absolute inset-0 rounded-full bg-foreground shadow-sm"
                transition={{ type: 'spring', stiffness: 520, damping: 34, mass: 0.6 }}
                style={{ zIndex: -1 }}
              />
            )}
            {code.toUpperCase()}
          </button>
        );
      })}
    </div>
  );
};

export default LanguageSwitch;
